import type { GroupTotals, TopCondition } from "../../types";
import { OverrideMultiplierInput } from "./OverrideMultiplierInput";
import { PriorYearCard } from "./PriorYearCard";
import { TopConditionsCard } from "./TopConditionsCard";
import { formatCurrency, formatInteger, formatRmm } from "../../utils/format";

interface GroupSummaryBandProps {
  groupId: string;
  totals: GroupTotals;
  multiplier: number;
  isLocked: boolean;
  /** Renewal Opportunities get the Prior Year panel; new business does not. */
  isRenewal: boolean;
  topConditions: readonly TopCondition[];
  /** The API is recalculating after a save (an exclude, an override); the numbers dim until its answer replaces them. */
  recalculating?: boolean;
  onPreviewMultiplier: (multiplier: number) => void;
  onCommitMultiplier: (multiplier: number) => Promise<boolean>;
  children?: never;
}

interface CountLineProps {
  label: string;
  value: string;
  emphasis?: boolean;
}

function CountLine({ label, value, emphasis = false }: CountLineProps) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-xs text-gray-500">{label}</dt>
      <dd className={`tabular-nums ${emphasis ? "text-lg font-semibold text-brand-green-dark" : "text-sm font-medium text-brand-navy"}`}>{value}</dd>
    </div>
  );
}

/**
 * One compact band above the members grid: member counts, debit totals with the
 * override multiplier, top conditions, and (renewals only) the prior year panel.
 * Keeps the grid full width.
 */
export function GroupSummaryBand({ groupId, totals, multiplier, isLocked, isRenewal, topConditions, recalculating = false, onPreviewMultiplier, onCommitMultiplier }: GroupSummaryBandProps) {
  return (
    <div role="group" aria-label="Group totals" aria-busy={recalculating} className={`grid gap-3 transition-opacity md:grid-cols-2 ${recalculating ? "opacity-60" : ""} ${isRenewal ? "xl:grid-cols-4" : "xl:grid-cols-3"}`}>
      <section aria-label="Member counts" className="rounded-2xl bg-white p-4 shadow-md">
        <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Members</h2>
        <dl className="space-y-1">
          <CountLine label="Employees" value={formatInteger(totals.employees)} />
          <CountLine label="Spouses" value={formatInteger(totals.spouses)} />
          <CountLine label="Children" value={formatInteger(totals.children)} />
          <CountLine label="Total Members" value={formatInteger(totals.totalMembers)} emphasis />
          <CountLine label="Members Excluded" value={formatInteger(totals.membersExcluded)} />
        </dl>
      </section>
      <section aria-label="Debits and RMM" className="rounded-2xl bg-white p-4 shadow-md">
        <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Debits and RMM</h2>
        <dl className="space-y-1">
          <CountLine label="Total Debits" value={formatInteger(totals.totalDebits)} />
          <CountLine label="RMM" value={formatRmm(totals.rmm)} emphasis />
          <CountLine label="Adjusted RMM" value={formatRmm(totals.adjustedRmm)} emphasis />
          {totals.uwDollars > 0 && <CountLine label="UW Dollars" value={formatCurrency(totals.uwDollars)} />}
        </dl>
        <div className="mt-2">
          <OverrideMultiplierInput value={multiplier} disabled={isLocked} onPreview={onPreviewMultiplier} onCommit={onCommitMultiplier} />
        </div>
      </section>
      <TopConditionsCard groupId={groupId} conditions={topConditions} />
      {isRenewal && <PriorYearCard groupId={groupId} />}
    </div>
  );
}
